/**
 * Decode-lag episode contract + the ladder-input dedup.
 *
 * Field data (webrtc-debug.ndjson) showed the Android failure shape: a sustained decode
 * backlog (framesReceived − framesDecoded) growing for seconds while RTT stays flat and
 * loss is zero. That backlog feeds TWO ladder inputs — `playoutLatencyTrendMs` (the
 * backlog converted to playback time) and `decodeBacklogFrames` (the same backlog in
 * frames) — and `evaluateCongestionSignal` treated them as independent distance-independent
 * signals, so one backlog could fire two `critical` verdicts in the same tick. This module
 * defines the canonical episode and the normalization that collapses the two inputs into
 * ONE backlog signal before the ladder evaluates, so one backlog never double-fires.
 *
 * Dwell/growth constants are shared by the guest detector (Phase 2) and the host ladder
 * fix (Phase 3), so a single number governs both.
 */

/** How long decode-lag must persist before the ladder or a downgrade can fire. */
export const DECODE_LAG_DWELL_MS = 2_000;

/** Minimum backlog growth (ms of playback time) that counts as a decode-lag episode at all. */
export const DECODE_LAG_MIN_GROWTH_MS = 100;

/**
 * Decode FPS below this fraction of the rate actually DELIVERED to the guest is a shortfall:
 * the decoder is emitting fewer frames than arrived. Above it, an active episode is standing
 * depth (frames queued in the pipeline) with a decoder that IS keeping pace — and stepping
 * fps down makes that worse, not better: a hardware decoder holds a fixed number of frames
 * in flight, so the same depth costs twice the milliseconds at 30fps that it did at 60.
 *
 * The denominator must be frames DELIVERED, never frames intended. Measured against the
 * encoder's *target*, this ratio is self-fulfilling: a guest cannot decode frames the host
 * never sent, so any host-side cut drives decodeFps down, which reads as a deeper deficit,
 * which justifies the next cut. Field data: a host that had ratcheted itself to 17fps encode
 * kept reading its guest's honest 18fps decode as a 40% deficit for ten minutes.
 */
export const DECODE_LAG_THROUGHPUT_DEFICIT_RATIO = 0.85;

/**
 * Decoder duty cycle (fraction of wall time spent inside decode) at or above which the
 * decoder is plausibly the bottleneck, so trading frames for headroom is a real remedy.
 *
 * This is the only signal that distinguishes "the decoder cannot keep up" from "the decoder
 * was not given much to do", and unlike any frame-rate comparison it cannot be moved by the
 * host's own send rate. Field data: an Android decoder sat on a 14-frame queue at 12 ms per
 * frame and 18 fps — a 22% duty cycle, idle more than three quarters of the time — while
 * every rate-based test called it a throughput deficit and cut its framerate for it.
 */
export const DECODER_SATURATION_RATIO = 0.75;

/**
 * Fraction of wall time the decoder spent decoding, from its own per-frame residency.
 * `null` when the engine did not report enough to tell (no rate, or no residency).
 */
export function decoderDutyCycle(decodeFps: number, decodeMsPerFrame: number | undefined): number | null {
  if (!Number.isFinite(decodeFps) || decodeFps <= 0) return null;
  if (typeof decodeMsPerFrame !== 'number' || !Number.isFinite(decodeMsPerFrame) || decodeMsPerFrame < 0) {
    return null;
  }
  return (decodeFps * decodeMsPerFrame) / 1_000;
}

/**
 * Presented fps below this fraction of DECODED fps is a presentation deficit: frames are
 * being decoded and then discarded at render because they arrived past their deadline.
 * Field shape (Safari, 2026-09-26): decode 59-62 while presenting 28-46 — every one of those
 * missing frames reached the decoder and never reached the glass. Unlike the frame-counter
 * backlog (which WebKit cannot measure — no `framesReceived`), rVFC presentations exist on
 * every engine, so this is the decode-side signal Safari-class guests can emit.
 */
export const PRESENT_DEFICIT_RATIO = 0.75;

/** The canonical decode-lag episode: a sustained decode-side backlog growing past dwell. */
export type DecodeLagEpisode = {
  /** Backlog growth over the sample interval, frames (Δreceived − Δdecoded). */
  backlogFrames: number;
  /** Growth of the backlog-derived playout delay above the window minimum (ms). */
  playoutLatencyTrendMs: number;
  /** Current decode framerate. */
  decodeFps: number;
  /** True while the episode is active (backlog persists past dwell + growth floor). */
  active: boolean;
  /**
   * Frames received but not yet decoded, ABSOLUTE (framesReceived − framesDecoded, less
   * any the engine reports dropped). The decoder's standing depth: what a player actually
   * waits behind. Optional on the wire — older guests omit it.
   */
  standingBacklogFrames?: number;
  /**
   * Wall time an average frame spent inside the decoder this interval (ms). Turns the
   * episode from a set of rates into a statement about the decoder's own capacity.
   * Optional on the wire — engines that do not report `totalDecodeTime` omit it.
   */
  decodeMsPerFrame?: number;
  /**
   * What reached the glass this interval (rVFC presentations per second). Present ONLY on
   * a presentation-deficit episode — one produced from rVFC evidence because the engine
   * cannot measure the frame-counter backlog (Safari). Its presence tells the host the
   * deficit is at RENDER, not in the decoder: frames arrive fine and die at the display
   * deadline, so the remedy is the lower tier (smaller frames arrive sooner), never the
   * fps ladder. Optional on the wire — older hosts ignore it.
   */
  presentedFps?: number;
};

/** Guard the episode shape for any wire transport that carries it. */
export function isDecodeLagEpisode(v: unknown): v is DecodeLagEpisode {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  const standing = e['standingBacklogFrames'];
  const residency = e['decodeMsPerFrame'];
  const presented = e['presentedFps'];
  return (
    typeof e['backlogFrames'] === 'number' &&
    typeof e['playoutLatencyTrendMs'] === 'number' &&
    typeof e['decodeFps'] === 'number' &&
    typeof e['active'] === 'boolean' &&
    (standing === undefined || (typeof standing === 'number' && Number.isFinite(standing))) &&
    (residency === undefined || (typeof residency === 'number' && Number.isFinite(residency))) &&
    (presented === undefined || (typeof presented === 'number' && Number.isFinite(presented)))
  );
}

/**
 * True when the episode's decoder is failing to keep up with the rate it is actually sent —
 * the only shape a framerate step-down can help. A decoder at or near the delivered rate, or
 * one with idle time to spare, is a depth problem, and fps must not be cut for it.
 *
 * Two independent conditions, because either one alone has a blind spot. The rate shortfall
 * is measured against frames DELIVERED (`deliveredFps`), never the host's intended target,
 * so a host that has cut its own send rate cannot read the resulting low decode rate as the
 * guest's fault. Residency then has the final say wherever the engine reports it: a decoder
 * idling three quarters of the time is not the bottleneck no matter what the rates say.
 */
export function isDecodeThroughputDeficit(reading: {
  decodeFps?: number;
  /** Frames per second actually put on the wire for this peer. Never the intended target. */
  deliveredFps: number;
  decodeMsPerFrame?: number;
}): boolean {
  const { decodeFps, deliveredFps, decodeMsPerFrame } = reading;
  if (typeof decodeFps !== 'number' || !Number.isFinite(decodeFps)) return false;
  if (!Number.isFinite(deliveredFps) || deliveredFps <= 0) return false;
  if (decodeFps >= deliveredFps * DECODE_LAG_THROUGHPUT_DEFICIT_RATIO) return false;
  const dutyCycle = decoderDutyCycle(decodeFps, decodeMsPerFrame);
  return dutyCycle === null || dutyCycle >= DECODER_SATURATION_RATIO;
}

/** `isDecodeThroughputDeficit` for an episode off the wire. */
export function hasDecodeThroughputDeficit(episode: DecodeLagEpisode, deliveredFps: number): boolean {
  return isDecodeThroughputDeficit({
    decodeFps: episode.decodeFps,
    deliveredFps,
    ...(episode.decodeMsPerFrame !== undefined ? { decodeMsPerFrame: episode.decodeMsPerFrame } : {}),
  });
}
